import { useEffect, useMemo, useState } from 'react';
import { hostListProperties } from '../api/host';
import { HostSection } from '../components/host/HostLayout';
import { useToast } from '../components/ToastContext';
import { hostCancellation } from '../services/cancellationsApi';

const tabs = [
  { key: 'requests', label: 'Requests', status: '' },
  { key: 'approved', label: 'Approved', status: 'approved' },
  { key: 'rejected', label: 'Rejected', status: 'rejected' },
  { key: 'refunds', label: 'Refunds', status: 'refunded' },
  { key: 'policies', label: 'Policies', status: null },
];

const toneMap = {
  requested: 'bg-amber-50 text-amber-700 ring-amber-100',
  host_reviewed: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
  admin_reviewed: 'bg-blue-50 text-blue-700 ring-blue-100',
  approved: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  rejected: 'bg-red-50 text-red-700 ring-red-100',
  refund_initiated: 'bg-sky-50 text-sky-700 ring-sky-100',
  refunded: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  disputed: 'bg-orange-50 text-orange-700 ring-orange-100',
};

const StatusChip = ({ status }) => {
  const cls = toneMap[status] || 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${cls}`}>
      {status?.replace(/_/g, ' ') || 'unknown'}
    </span>
  );
};

const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : 'TBD');
  const formatRange = (start, end) => `${formatDate(start)} to ${formatDate(end)}`;

const RefundBreakdown = ({ snapshot, currency }) => {
  if (!snapshot) return null;
  const lines = snapshot.breakdown || [];
  return (
    <div className="space-y-1">
      {lines.map((line, idx) => (
        <div key={line.label || idx} className="flex justify-between text-sm text-slate-700">
          <span>{line.label}</span>
          <span>
            {currency} {line.amount}
          </span>
        </div>
      ))}
      <div className="flex justify-between font-semibold text-slate-900 pt-2 border-t border-slate-100">
        <span>Refundable</span>
        <span>
          {currency} {snapshot.refundable_amount || 0}
        </span>
      </div>
      <div className="flex justify-between text-xs text-slate-500">
        <span>Non refundable</span>
        <span>
          {currency} {snapshot.non_refundable_amount || 0}
        </span>
      </div>
    </div>
  );
};

const Timeline = ({ events }) => {
  if (!events?.length) return <div className="text-sm text-slate-500">No timeline events yet.</div>;
  return (
    <div className="space-y-3">
      {events.map((evt) => (
        <div key={evt.id} className="flex gap-3">
          <div className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
          <div>
            <div className="text-sm font-semibold text-slate-800 capitalize">
              {evt.event_type.replace(/_/g, ' ')} {evt.to_status ? `(${evt.to_status})` : ''}
            </div>
            <div className="text-xs text-slate-500">{new Date(evt.created_at).toLocaleString()}</div>
            {evt.metadata?.reason && <div className="text-xs text-slate-600">Reason: {evt.metadata.reason}</div>}
            {evt.metadata?.message && <div className="text-xs text-slate-600">Note: {evt.metadata.message}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

const DetailDrawer = ({
  detail,
  onClose,
  onAgree,
  onDeny,
  onMessage,
  actionLoading = false,
}) => {
  const [denyNote, setDenyNote] = useState('');
  const [message, setMessage] = useState('');
  if (!detail) return null;
  const { cancellation, booking, refunds, events } = detail;
  const latestRefund = refunds?.[0];
  const refundSnapshot = cancellation?.refund_snapshot || {};

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-slate-900/40 backdrop-blur-sm">
      <div className="h-full w-full max-w-4xl overflow-y-auto bg-white shadow-xl ring-1 ring-black/5">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-700">Cancellation</p>
            <p className="text-lg font-bold text-slate-900">{booking?.booking_code}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
        <div className="grid gap-6 p-6 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Booking</p>
                  <p className="text-base font-semibold text-slate-900">{booking?.property_title}</p>
                  <p className="text-sm text-slate-600">{booking?.guest_name || 'Guest'}</p>
                </div>
                <StatusChip status={cancellation?.status} />
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-700">
                <div className="flex justify-between">
                  <span>Dates</span>
                  <span>{formatRange(booking?.check_in_date, booking?.check_out_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Payment</span>
                  <span className="capitalize">{booking?.payment_status || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Amount</span>
                  <span>
                    {booking?.currency} {booking?.total_amount || booking?.totals_json?.total || 0}
                  </span>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Cancellation</p>
                  <p className="text-base font-semibold text-slate-900">{cancellation?.initiated_by} initiated</p>
                  <p className="text-xs text-slate-500">
                    Requested at {cancellation?.requested_at ? new Date(cancellation.requested_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <StatusChip status={cancellation?.status} />
              </div>
              {cancellation?.reason && (
                <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                  <p className="font-semibold text-slate-800">Reason</p>
                  <p>{cancellation.reason}</p>
                </div>
              )}
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-100 p-3">
                  <p className="text-xs text-slate-500">Policy</p>
                  <p className="text-sm font-semibold text-slate-900">{cancellation?.policy_snapshot?.name || 'Policy'}</p>
                  <p className="text-xs text-slate-500 uppercase">{cancellation?.policy_snapshot?.code}</p>
                </div>
                <div className="rounded-lg border border-slate-100 p-3">
                  <p className="text-xs text-slate-500">Refund amount</p>
                  <p className="text-lg font-bold text-emerald-700">
                    {cancellation?.currency} {cancellation?.refund_amount || 0}
                  </p>
                </div>
              </div>
              <div className="mt-3">
                <p className="text-sm font-semibold text-slate-800">Refund breakdown</p>
                <RefundBreakdown snapshot={refundSnapshot} currency={cancellation?.currency || booking?.currency} />
              </div>
              {latestRefund && (
                <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-slate-800">Refund status</p>
                  <p className="text-xs text-slate-600">
                    {latestRefund.status} {latestRefund.reference ? `· Ref ${latestRefund.reference}` : ''}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800">Host actions</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={onAgree}
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Agree to cancel
                  </button>
                  <button
                    type="button"
                    disabled={actionLoading || !denyNote}
                    onClick={() => onDeny(denyNote)}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Deny
                  </button>
                </div>
              </div>
              <textarea
                value={denyNote}
                onChange={(e) => setDenyNote(e.target.value)}
                placeholder="Add note for denial"
                className="mt-3 w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
                rows={2}
              />
              <div className="mt-3 space-y-2">
                <p className="text-sm font-semibold text-slate-800">Message Admin</p>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a note to admin"
                  className="w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
                  rows={2}
                />
                <button
                  type="button"
                  disabled={!message || actionLoading}
                  onClick={() => onMessage(message).then(() => setMessage(''))}
                  className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  Send note
                </button>
              </div>
              {cancellation?.host_action && (
                <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">
                  <p className="font-semibold text-slate-800">Your decision</p>
                  <p className="capitalize">{cancellation.host_action}</p>
                  {cancellation.host_action_note && <p className="text-xs text-slate-500">{cancellation.host_action_note}</p>}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-semibold text-slate-800">Timeline</p>
              <Timeline events={events?.filter((evt) => ['cancel', 'refund'].some((k) => evt.event_type.includes(k))) || events} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HostCancellations = ({ accessToken }) => {
  const [activeTab, setActiveTab] = useState('requests');
  const [filters, setFilters] = useState({ status: '', property_id: '', date_from: '', date_to: '', q: '' });
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const toast = useToast();

  const loadProperties = async () => {
    try {
      const res = await hostListProperties(accessToken, { limit: 100 });
      setProperties(res.items || []);
    } catch (_) {
      // ignore
    }
  };

  const loadList = async () => {
    if (activeTab === 'policies') return;
    setLoading(true);
    setError('');
    try {
      const res = await hostCancellation.list(accessToken, filters);
      setItems(res.items || res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load cancellations');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    try {
      const res = await hostCancellation.get(id, accessToken);
      setDetail(res);
    } catch (err) {
      toast.error(err.message || 'Failed to load detail');
    }
  };

  const handleRowClick = async (row) => {
    setDetail(null);
    await loadDetail(row.id);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab.key);
    const next = { ...filters };
    next.status = tab.status !== null ? tab.status : '';
    setFilters(next);
  };

  const exportCsv = () => {
    if (!items.length) return;
    const header = ['Booking Code', 'Property', 'Guest', 'Status', 'Refund Amount', 'Requested At'];
    const rows = items.map((it) => [
      it.booking_code,
      it.property_title,
      it.guest_name,
      it.status,
      it.refund_amount,
      it.requested_at,
    ]);
    const csv = [header, ...rows].map((r) => r.map((cell) => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cancellations.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const performAction = async (cb, successMsg) => {
    setActionLoading(true);
    try {
      await cb();
      await loadList();
      if (detail?.cancellation?.id) await loadDetail(detail.cancellation.id);
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tab = tabs.find((t) => t.key === activeTab);
    const nextFilters = { ...filters, status: tab?.status !== null ? tab?.status || '' : '' };
    setFilters(nextFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.property_id, filters.date_from, filters.date_to, filters.q]);

  const policyView = useMemo(() => {
    if (activeTab !== 'policies') return null;
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">Property policies</div>
        <div className="divide-y divide-slate-100">
          {properties.map((p) => (
            <div key={p.id} className="px-4 py-3 flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-800">{p.title}</p>
                <p className="text-xs text-slate-500">Code: {p.cancellation_policy || 'FLEXIBLE'}</p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                {p.cancellation_policy || 'Flexible'}
              </span>
            </div>
          ))}
          {properties.length === 0 && <div className="p-4 text-sm text-slate-600">No properties found.</div>}
        </div>
      </div>
    );
  }, [activeTab, properties]);

  return (
    <>
      <HostSection
        title="Cancellations & Refunds"
        description="Track cancellation requests, policy application, and refunds."
        action={
          <button
            type="button"
            onClick={exportCsv}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Export CSV
          </button>
        }
      >
        <div className="flex flex-wrap gap-2 border-b border-slate-200 px-4 pt-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabChange(tab)}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${
                activeTab === tab.key ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab !== 'policies' && (
          <div className="space-y-3 p-4">
            <div className="grid gap-3 md:grid-cols-4">
              <select
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
              >
                <option value="">All statuses</option>
                <option value="requested">Requested</option>
                <option value="host_reviewed">Host reviewed</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="refund_initiated">Refund initiated</option>
                <option value="refunded">Refunded</option>
              </select>
              <select
                value={filters.property_id}
                onChange={(e) => setFilters((f) => ({ ...f, property_id: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
              >
                <option value="">All properties</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
              />
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
              />
              <div className="md:col-span-2">
                <input
                  type="search"
                  placeholder="Search booking code or guest"
                  value={filters.q}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
                <span>Cancellation requests</span>
                {loading && <span className="text-xs font-normal text-slate-500">Loading...</span>}
              </div>
              {error && <div className="px-4 py-3 text-sm text-red-600">{error}</div>}
              {!loading && !items.length && !error && (
                <div className="p-6 text-sm text-slate-600">No cancellation requests yet.</div>
              )}
              {items.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Booking</th>
                        <th className="px-4 py-3">Property</th>
                        <th className="px-4 py-3">Guest</th>
                        <th className="px-4 py-3">Dates</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Refund</th>
                        <th className="px-4 py-3">Requested</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {items.map((item) => (
                        <tr
                          key={item.id}
                          className="hover:bg-emerald-50/40 cursor-pointer"
                          onClick={() => handleRowClick(item)}
                        >
                          <td className="px-4 py-3 font-semibold text-slate-900">{item.booking_code}</td>
                          <td className="px-4 py-3 text-slate-700">{item.property_title}</td>
                          <td className="px-4 py-3 text-slate-700">{item.guest_name || 'Guest'}</td>
                          <td className="px-4 py-3 text-slate-700">{formatRange(item.check_in_date, item.check_out_date)}</td>
                          <td className="px-4 py-3">
                            <StatusChip status={item.status} />
                          </td>
                          <td className="px-4 py-3 text-slate-900 font-semibold">
                            {item.currency} {item.refund_amount || 0}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-xs">
                            {item.requested_at ? new Date(item.requested_at).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {policyView}
      </HostSection>

      <DetailDrawer
        detail={detail}
        onClose={() => setDetail(null)}
        onAgree={() => performAction(() => hostCancellation.agree(detail.cancellation.id, accessToken), 'Agreed to cancel')}
        onDeny={(note) => performAction(() => hostCancellation.deny(detail.cancellation.id, note, accessToken), 'Response saved')}
        onMessage={(message) => performAction(() => hostCancellation.messageAdmin(detail.cancellation.id, message, accessToken), 'Note sent')}
        actionLoading={actionLoading}
      />
    </>
  );
};

export default HostCancellations;

